import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, API, idr } from "../lib/api";
import {
  Plus, Trash2, X, Download, Smartphone, Phone, Send, Building,
  Pencil, Clock, Lock, History, FileText, Info, Upload,
  AlertTriangle, Calculator, ClipboardList, ChevronDown, ChevronUp,
  ArrowLeftRight, Wallet, PieChart,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import InputRupiah from "../components/InputRupiah";
import { useCabangOptions } from "../lib/cabang";
import { DatePicker, MonthPicker } from "@/components/ui/date-picker";
import LoadingState from "@/components/ui/loading-state";
import { hariIniISO, bulanIniISO, formatTanggal } from "@/lib/tanggal";
import { useSortState, sortRows, SortTh } from "@/components/ui/sort";

const QC_STATUS = ["Passed", "Failed", "Perlu Perbaikan", "Pending"];
const STORAGES = ["", "64GB", "128GB", "256GB", "512GB", "1TB"];
const GRADES = [
  { v: "", label: "— grade —" },
  { v: "A", label: "A — Mulus" },
  { v: "B", label: "B — Normal" },
  { v: "C", label: "C — Pemakaian berat" },
  { v: "D", label: "D — Rusak / minus" },
];
// 6 fungsi yang dicek cepat sebagai toggle; layar/body/baterai/iCloud punya input sendiri
const CHECK_TOGGLES = [
  ["face_id_working", "Face ID"],
  ["buttons_working", "Tombol"],
  ["camera_working", "Kamera"],
  ["speaker_working", "Speaker"],
  ["charging_working", "Charging"],
  ["other_functions", "Fungsi lain"],
];
const ICLOUD_LABEL = { "": "Belum dicek", sudah_logout: "Sudah logout", belum_logout: "BELUM logout" };
const FINDMY_LABEL = { "": "Belum dicek", nonaktif: "Nonaktif", masih_aktif: "MASIH AKTIF" };
const emptyChecklist = () => ({
  screen_condition: "Mulus", body_condition: "Mulus", battery_health: "",
  face_id_working: true, buttons_working: true, camera_working: true,
  speaker_working: true, charging_working: true, other_functions: true,
  overall_grade: "", icloud_status: "", find_my_status: "",
});

const nowTime = () => {
  const d = new Date();
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
};

const emptyForm = () => ({
  id: null,
  tanggal: hariIniISO(),
  waktu: nowTime(),
  nama_pelanggan: "", no_hp: "", no_rekening: "", nama_bank: "",
  type_hp: "", imei: "", storage: "", grade: "",
  harga_akuisisi: 0, qc_check: "Pending", qc_notes: "",
  catatan: "", nama_penerima: "", branch: "", kode: "HP",
  // Task C (poin 6 owner): akun sumber pembayaran akuisisi — opsional, kosong
  // = fallback ke resolusi metode+brand lama (lihat sales_posting.py).
  sumber_akun_id: "",
  // Task D (poin 7 owner): jenis akuisisi ANDAL — Terima Jual (default) vs
  // Trade-in. Trade-in items mirip InputNotaAkuisisi.jsx supaya kebiasaan
  // input sama di kedua jalur (nota & langsung).
  trade_in: false, trade_in_note: "",
  trade_in_items: [{ nama: "", harga: 0, imei: "" }],
});

const qcStyle = {
  Passed: "bg-emerald-100 text-emerald-800",
  Failed: "bg-rose-100 text-rose-800",
  "Perlu Perbaikan": "bg-amber-100 text-amber-800",
  Pending: "bg-slate-100 text-slate-700",
};

const canEditNow = (createdAt, windowMin) => {
  if (!createdAt) return false;
  try {
    return (Date.now() - new Date(createdAt).getTime()) / 60000 <= (windowMin || 60);
  } catch { return false; }
};
const minutesLeft = (createdAt, windowMin) => {
  if (!createdAt) return 0;
  try {
    return Math.max(0, Math.ceil((windowMin || 60) - (Date.now() - new Date(createdAt).getTime()) / 60000));
  } catch { return 0; }
};

export default function Acquisitions() {
  const { user } = useAuth();
  const BRANCHES = useCabangOptions({ denganKosong: true });
  const canEdit = user?.role !== "viewer";
  const [items, setItems] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [periode, setPeriode] = useState(bulanIniISO());
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [branch, setBranch] = useState("");
  const [modal, setModal] = useState(false);
  const [modalMode, setModalMode] = useState("create");
  const [form, setForm] = useState(emptyForm());
  const [detailId, setDetailId] = useState(null);
  const [detail, setDetail] = useState(null);
  // Deep-link ke satu transaksi (brief bos 14 Agu #2, pola SAMA dengan
  // `Sales.jsx::openLinkedSale`) — RekonAkuisisi.jsx menautkan nomor invoice
  // baris akuisisi ke `/acquisitions?id=<acquisition_id>`. Pesan ini
  // menjelaskan KENAPA transaksi tertaut gagal dibuka (dihapus / tak berhak /
  // error lain) supaya pengguna tidak mendarat di halaman kosong dan
  // mengira datanya hilang.
  const [searchParams, setSearchParams] = useSearchParams();
  const [linkedAcqNotice, setLinkedAcqNotice] = useState(null);
  const [editWindow, setEditWindow] = useState(60);
  const [saving, setSaving] = useState(false);
  // Mesin harga & checklist kondisi — semuanya opsional, alur input lama tetap jalan
  const [checklist, setChecklist] = useState(emptyChecklist());
  const [showChecklist, setShowChecklist] = useState(false);
  const [est, setEst] = useState(null);        // hasil /acquisitions/estimate terakhir
  const [estLoading, setEstLoading] = useState(false);
  const [estTipe, setEstTipe] = useState("trade_in");
  // PIN persetujuan hapus (brief #23) — retry() menyimpan aksi delete yang
  // sempat dibalas 428, dipanggil ulang begitu PIN dikirim.
  const [pinDialog, setPinDialog] = useState(null); // {pin, submitting, error, retry}
  // Task C — akun kas/bank/petty cash aktif, dipakai dropdown "Akun Sumber
  // Pembayaran" (endpoint sudah ada, dipakai juga oleh FinancePettyCash dkk).
  const [kasBankAccounts, setKasBankAccounts] = useState([]);
  // Task D/poin 7 — rekap per jenis akuisisi (Terima Jual vs Trade-in)
  const [rekapJenis, setRekapJenis] = useState(null);
  // Pengurutan tabel (client-side, lihat components/ui/sort.jsx) — urutan
  // bawaan tetap seperti sebelumnya.
  const acqSort = useSortState(null, "asc");

  const load = useCallback(() => {
    const p = new URLSearchParams();
    if (dateFrom || dateTo) {
      if (dateFrom) p.set("date_from", dateFrom);
      if (dateTo) p.set("date_to", dateTo);
    } else if (periode) {
      p.set("periode", periode);
    }
    if (branch) p.set("branch", branch);
    api.get(`/acquisitions?${p}`).then(r => setItems(r.data));
    api.get(`/acquisitions/rekap-jenis?${p}`).then(r => setRekapJenis(r.data)).catch(() => setRekapJenis(null));
  }, [periode, dateFrom, dateTo, branch]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/employees").then(r => {
      const list = (r.data || []).filter(e => e.is_active !== false)
        .sort((a, b) => a.nama.localeCompare(b.nama));
      setEmployees(list);
    }).catch(() => setEmployees([]));
    api.get("/config").then(r => setEditWindow(r.data?.edit_window_minutes || 60)).catch(() => {});
    api.get("/finance/accounts/kas-bank").then(r => setKasBankAccounts(r.data || [])).catch(() => setKasBankAccounts([]));
  }, []);

  const branchEmployees = form.branch
    ? employees.filter(e => !e.branch || e.branch === form.branch)
    : employees;

  // Task D — total nilai unit trade-in di form (mirror InputNotaAkuisisi.jsx)
  const tradeInTotal = useMemo(
    () => form.trade_in ? (form.trade_in_items || []).reduce((s, t) => s + (parseFloat(t.harga) || 0), 0) : 0,
    [form.trade_in, form.trade_in_items]
  );
  const addTradeItem = () => setForm(f => ({ ...f, trade_in_items: [...(f.trade_in_items || []), { nama: "", harga: 0, imei: "" }] }));
  const rmTradeItem = (i) => setForm(f => ({ ...f, trade_in_items: (f.trade_in_items || []).filter((_, j) => j !== i) }));
  const setTradeItem = (i, patch) => setForm(f => {
    const arr = [...(f.trade_in_items || [])];
    arr[i] = { ...arr[i], ...patch };
    return { ...f, trade_in_items: arr };
  });

  const openCreate = () => {
    setModalMode("create"); setForm(emptyForm());
    setChecklist(emptyChecklist()); setShowChecklist(false);
    setEst(null); setEstTipe("trade_in");
    setModal(true);
  };
  const openEdit = (a) => {
    setModalMode("edit");
    setForm({
      id: a.id,
      tanggal: a.tanggal || hariIniISO(),
      waktu: a.waktu || nowTime(),
      nama_pelanggan: a.nama_pelanggan || "",
      no_hp: a.no_hp || "",
      no_rekening: a.no_rekening || "",
      nama_bank: a.nama_bank || "",
      type_hp: a.type_hp || "",
      imei: a.imei || "",
      storage: a.storage || "",
      grade: a.grade || "",
      harga_akuisisi: a.harga_akuisisi || 0,
      qc_check: a.qc_check || "Pending",
      qc_notes: a.qc_notes || "",
      catatan: a.catatan || "",
      nama_penerima: a.nama_penerima || "",
      branch: a.branch || "",
      kode: a.kode || "HP",
      sumber_akun_id: a.sumber_akun_id || "",
      trade_in: !!a.trade_in, trade_in_note: a.trade_in_note || "",
      trade_in_items: a.trade_in_items?.length
        ? a.trade_in_items
        : [{ nama: "", harga: 0, imei: "" }],
    });
    // Prefill checklist lama supaya edit tidak menimpanya dengan default kosong
    setChecklist(a.checklist ? { ...emptyChecklist(), ...a.checklist, battery_health: a.checklist.battery_health ?? "" } : emptyChecklist());
    setShowChecklist(!!a.checklist);
    setEst(a.estimasi || null);
    setEstTipe(a.estimasi?.tipe || "trade_in");
    setDetail(null); setDetailId(null);
    setModal(true);
  };

  const cekEstimasi = async () => {
    if (!form.type_hp) { toast.error("Isi Type HP dulu untuk cek estimasi"); return; }
    setEstLoading(true);
    try {
      const { data } = await api.get("/acquisitions/estimate", {
        params: { model: form.type_hp, storage: form.storage, grade: form.grade || "B", tipe: estTipe },
      });
      setEst(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menghitung estimasi");
    } finally { setEstLoading(false); }
  };

  const save = async () => {
    if (!form.nama_pelanggan || !form.type_hp || !form.nama_penerima) {
      toast.error("Pelanggan, Type HP & Penerima wajib"); return;
    }
    setSaving(true);
    try {
      const payload = { ...form, waktu: form.waktu || nowTime() };
      delete payload.id;
      // Task D — trade_in_items hanya bermakna kalau trade_in dicentang.
      // Dikosongkan (null) kalau tidak, sama pola dengan InputNotaAkuisisi.jsx,
      // supaya backend (Optional[...] = None saat tidak dikirim) tidak
      // menyimpan baris kosong/tidak relevan.
      if (!form.trade_in) {
        payload.trade_in_items = null;
        payload.trade_in_note = "";
      } else {
        payload.trade_in_items = (form.trade_in_items || [])
          .filter(t => (t.nama || "").trim())
          .map(t => ({ nama: t.nama.trim(), harga: parseFloat(t.harga) || 0, imei: (t.imei || "").trim() }));
      }
      // Checklist hanya dikirim kalau bagiannya dibuka — kalau ditutup, backend
      // mempertahankan checklist lama (None = tidak diubah), bukan menghapusnya.
      if (showChecklist) {
        payload.checklist = {
          ...checklist,
          battery_health: checklist.battery_health === "" ? null : parseInt(checklist.battery_health, 10),
          overall_grade: form.grade || checklist.overall_grade || "",
        };
      }
      if (est) payload.estimasi = est; // snapshot estimasi yang dilihat penginput
      if (modalMode === "edit" && form.id) {
        await api.patch(`/acquisitions/${form.id}`, payload);
        toast.success("Perubahan tersimpan");
      } else {
        await api.post("/acquisitions", payload);
        toast.success("Akuisisi tercatat");
      }
      setModal(false); setForm(emptyForm()); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal");
    } finally { setSaving(false); }
  };

  const doDeleteAcq = async (id, pin) => {
    await api.delete(`/acquisitions/${id}`, pin ? { params: { pin } } : undefined);
    setDetailId(null); setDetail(null);
    load();
  };

  // Brief #23 — DELETE bisa membalas 428 (butuh PIN persetujuan pemegang izin
  // Pusat Approval). Ajukan otomatis, lalu buka dialog PIN.
  const del = async (id) => {
    if (!confirm("Yakin hapus akuisisi ini?")) return;
    try {
      await doDeleteAcq(id);
    } catch (e) {
      if (e?.response?.status === 428) {
        try {
          await api.post("/aktivitas/hapus/ajukan", {
            modul: "acquisitions", target_id: id,
            ringkasan: `Hapus akuisisi ${detail?.type_hp || ""}`.trim(),
          });
        } catch (e2) {
          toast.error(e2?.response?.data?.detail || "Gagal mengajukan izin hapus");
          return;
        }
        setPinDialog({ pin: "", submitting: false, error: "", retry: (pin) => doDeleteAcq(id, pin) });
      } else {
        toast.error(e?.response?.data?.detail || "Gagal menghapus akuisisi");
      }
    }
  };

  const submitPinDialog = async () => {
    if (!pinDialog) return;
    setPinDialog(d => ({ ...d, submitting: true, error: "" }));
    try {
      await pinDialog.retry(pinDialog.pin);
      setPinDialog(null);
    } catch (e) {
      if (e?.response?.status === 403) {
        setPinDialog(d => ({ ...d, submitting: false, error: "PIN salah atau sudah kedaluwarsa — coba lagi." }));
      } else {
        toast.error(e?.response?.data?.detail || "Gagal menghapus");
        setPinDialog(d => ({ ...d, submitting: false }));
      }
    }
  };

  const openDetail = async (id) => {
    setDetailId(id); setDetail(null);
    try {
      const { data } = await api.get(`/acquisitions/${id}`);
      setDetail(data);
    } catch { toast.error("Gagal memuat detail"); setDetailId(null); }
  };

  // Deep-link ke satu transaksi (brief bos 14 Agu #2) — dipanggil dari `?id=`
  // di URL. Diambil LANGSUNG dari API by id (bukan dicari di `items`, yang
  // cuma berisi halaman/filter periode yang sedang aktif), jadi transaksi
  // tetap terbuka walau ada di bulan/cabang lain di luar filter saat ini.
  // Kalau gagal, tampilkan alasan yang jelas lewat `linkedAcqNotice` — beda
  // dari `openDetail` biasa (toast singkat), karena tautan ini sering dibuka
  // sebagai satu-satunya aksi pengguna di halaman ini, dan toast bisa terlewat.
  const openLinkedAcq = useCallback(async (id) => {
    setDetailId(id); setDetail(null); setLinkedAcqNotice(null);
    try {
      const { data } = await api.get(`/acquisitions/${id}`);
      setDetail(data);
    } catch (e) {
      setDetailId(null); setDetail(null);
      const status = e?.response?.status;
      setLinkedAcqNotice(
        status === 404
          ? "Transaksi yang Anda tuju tidak ditemukan — kemungkinan sudah dihapus."
          : status === 403
          ? "Anda tidak berhak melihat transaksi ini."
          : (e?.response?.data?.detail || "Gagal memuat transaksi yang dituju. Coba lagi.")
      );
    } finally {
      // Bersihkan `?id=` dari URL setelah dicoba (berhasil maupun gagal) —
      // supaya refresh/tombol Kembali browser tidak mengulang percobaan yang
      // sama, dan supaya menutup lalu membuka modal lain tidak "menyalakan
      // ulang" tautan lama.
      setSearchParams(prev => {
        const next = new URLSearchParams(prev);
        next.delete("id");
        return next;
      }, { replace: true });
    }
  }, [setSearchParams]);

  const linkedAcqId = searchParams.get("id");
  useEffect(() => {
    if (linkedAcqId) openLinkedAcq(linkedAcqId);
  }, [linkedAcqId, openLinkedAcq]);

  const exportExcel = async () => {
    const p = new URLSearchParams();
    if (periode) p.set("periode", periode);
    if (branch) p.set("branch", branch);
    const token = localStorage.getItem("hris_token");
    const res = await fetch(`${API}/acquisitions/export?${p}`, { headers: { Authorization: `Bearer ${token}` } });
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `Akuisisi_${periode}.xlsx`;
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
  };

  const exportDaily = async () => {
    const p = new URLSearchParams();
    if (periode) p.set("periode", periode);
    if (branch) p.set("branch", branch);
    const token = localStorage.getItem("hris_token");
    const res = await fetch(`${API}/export/acquisitions-daily?${p}`, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) { toast.error("Gagal export"); return; }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `Akuisisi_Daily_${periode}.xlsx`;
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    toast.success("Export per-hari (31 sheet) sedang diunduh");
  };

  const totalAkuisisi = items.reduce((a, x) => a + (x.harga_akuisisi || 0), 0);

  return (
    <div className="p-4 md:p-8 max-w-[1400px]">
      <header className="flex items-start justify-between mb-6 flex-wrap gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-emerald-600 mb-2">Akuisisi</p>
          <h1 className="font-display text-2xl md:text-3xl font-bold text-slate-900">Akuisisi Unit Bekas</h1>
          <p className="mt-2 text-sm text-slate-500 max-w-2xl">
            <Info className="w-3.5 h-3.5 inline mb-0.5 mr-1 text-slate-400" />
            Klik <b>baris</b> untuk melihat detail. Setelah simpan, Anda bisa <b>edit</b> dalam <b>{editWindow} menit</b>.
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={async () => {
            const token = localStorage.getItem("hris_token");
            const res = await fetch(`${API}/import-templates/acquisitions`, { headers: { Authorization: `Bearer ${token}` } });
            if (!res.ok) { toast.error("Gagal download template"); return; }
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a"); a.href = url; a.download = "Template_Akuisisi.xlsx";
            document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
          }} data-testid="btn-template-acq"
            className="inline-flex items-center gap-2 h-10 px-4 rounded-lg border border-emerald-200 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 text-sm font-medium">
            <FileText className="w-4 h-4" /> Template
          </button>
          {canEdit && (
            <label data-testid="btn-import-acq"
              className="inline-flex items-center gap-2 h-10 px-4 rounded-lg border border-slate-300 bg-white hover:bg-slate-50 text-slate-700 text-sm font-medium cursor-pointer">
              <Upload className="w-4 h-4" /> Import
              <input type="file" accept=".xlsx,.xls" className="hidden"
                onChange={async (e) => {
                  const f = e.target.files?.[0]; if (!f) return;
                  const fd = new FormData(); fd.append("file", f);
                  try {
                    const { data } = await api.post("/acquisitions/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
                    toast.success(`Import: ${data.created} baru${data.errors.length ? `, ${data.errors.length} error` : ""}`);
                    load();
                  } catch (err) { toast.error(err?.response?.data?.detail || "Gagal import"); }
                  e.target.value = "";
                }} />
            </label>
          )}
          <button onClick={exportExcel} data-testid="btn-export-acq"
            className="inline-flex items-center gap-2 h-10 px-4 rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50 text-sm font-medium">
            <Download className="w-4 h-4" /> Excel
          </button>
          <button onClick={exportDaily} data-testid="btn-export-acq-daily"
            className="inline-flex items-center gap-2 h-10 px-4 rounded-lg border border-emerald-300 bg-emerald-50 hover:bg-emerald-100 text-emerald-800 text-sm font-medium">
            <Download className="w-4 h-4" /> Export Harian (Per Sheet)
          </button>
          {canEdit && (
            <button onClick={openCreate} data-testid="btn-add-acq"
              className="hidden">+</button>
          )}
        </div>
      </header>

      <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm mb-6 flex flex-wrap items-end gap-3">
        <label className="block">
          <span className="text-xs font-medium text-slate-600">Periode Bulan</span>
          <MonthPicker value={periode} onChange={v => { setPeriode(v); setDateFrom(""); setDateTo(""); }}
            data-testid="acq-periode"
            className="mt-1" />
        </label>
        <label className="block">
          <span className="text-xs font-medium text-slate-600">Dari Tanggal</span>
          <DatePicker value={dateFrom} onChange={setDateFrom}
            data-testid="acq-date-from"
            className="mt-1" />
        </label>
        <label className="block">
          <span className="text-xs font-medium text-slate-600">Sampai Tanggal</span>
          <DatePicker value={dateTo} onChange={setDateTo}
            data-testid="acq-date-to"
            className="mt-1" />
        </label>
        {(dateFrom || dateTo) && (
          <button onClick={() => { setDateFrom(""); setDateTo(""); }}
            data-testid="acq-reset-range"
            className="h-10 px-3 rounded-lg border border-slate-200 text-xs text-slate-600 hover:bg-slate-50">
            Reset Range
          </button>
        )}
        <label className="block">
          <span className="text-xs font-medium text-slate-600">Branch</span>
          <select value={branch} onChange={e => setBranch(e.target.value)}
            className="mt-1 h-10 px-3 rounded-lg border border-slate-200 text-sm bg-white min-w-[180px]">
            {BRANCHES.map(b => <option key={b} value={b}>{b || "Semua Branch"}</option>)}
          </select>
        </label>
        <div className="ml-auto flex gap-6 text-right">
          <div><p className="text-xs text-slate-500">Unit Diakuisisi</p><p className="font-display text-2xl font-bold text-slate-900">{items.length}</p></div>
          <div><p className="text-xs text-slate-500">Total Nilai</p><p className="font-display text-2xl font-bold text-slate-900">{idr(totalAkuisisi)}</p></div>
        </div>
      </div>

      {/* Rekap per Jenis (poin 7 owner): AU (Terima Jual) vs AU (Trade-in) —
          dikelompokkan lewat GET /acquisitions/rekap-jenis, sumbernya field
          `trade_in` yang kini andal untuk jalur nota MAUPUN langsung. */}
      {rekapJenis && (
        <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm mb-6 flex flex-wrap items-center gap-6"
          data-testid="panel-rekap-jenis-akuisisi">
          <div className="flex items-center gap-2 text-emerald-700 shrink-0">
            <PieChart className="w-4 h-4" />
            <p className="text-sm font-semibold text-slate-900">Rekap per Jenis Akuisisi</p>
          </div>
          <div className="grid grid-cols-2 gap-4 flex-1">
            <div data-testid="rekap-jenis-terima-jual">
              <p className="text-xs text-slate-500">AU (Terima Jual)</p>
              <p className="font-display text-lg font-bold text-slate-900">
                {rekapJenis.kelompok.terima_jual.n} unit · {idr(rekapJenis.kelompok.terima_jual.total)}
              </p>
            </div>
            <div data-testid="rekap-jenis-trade-in">
              <p className="text-xs text-slate-500 inline-flex items-center gap-1">
                <ArrowLeftRight className="w-3 h-3 text-violet-600" /> AU (Trade-in)
              </p>
              <p className="font-display text-lg font-bold text-violet-700">
                {rekapJenis.kelompok.trade_in.n} unit · {idr(rekapJenis.kelompok.trade_in.total)}
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 text-xs font-semibold text-slate-500 uppercase tracking-wider">
              <SortTh label="Waktu" sortKeyName="tanggal" sortKey={acqSort.sortKey} sortDir={acqSort.sortDir} onSort={acqSort.toggle} testid="acq-sort-waktu" />
              <SortTh label="Pelanggan" sortKeyName="nama_pelanggan" sortKey={acqSort.sortKey} sortDir={acqSort.sortDir} onSort={acqSort.toggle} testid="acq-sort-pelanggan" />
              <SortTh label="Type HP" sortKeyName="type_hp" sortKey={acqSort.sortKey} sortDir={acqSort.sortDir} onSort={acqSort.toggle} testid="acq-sort-typehp" />
              <th className="text-left py-3 px-4">Jenis</th>
              <th className="text-left py-3 px-4">Rekening</th>
              <SortTh label="Harga" sortKeyName="harga_akuisisi" sortKey={acqSort.sortKey} sortDir={acqSort.sortDir} onSort={acqSort.toggle} align="right" testid="acq-sort-harga" />
              <th className="text-left py-3 px-4">QC</th>
              <SortTh label="Penerima" sortKeyName="nama_penerima" sortKey={acqSort.sortKey} sortDir={acqSort.sortDir} onSort={acqSort.toggle} testid="acq-sort-penerima" />
              <th className="text-center py-3 px-4">Edit</th>
              <th className="text-right py-3 px-4">Status</th>
            </tr>
          </thead>
          <tbody>
            {sortRows(items, acqSort.sortKey, acqSort.sortDir).map(a => {
              const editable = canEditNow(a.created_at, editWindow);
              const editCount = a.edit_count || 0;
              return (
                <tr key={a.id} data-testid={`acq-${a.id}`}
                  onClick={() => openDetail(a.id)}
                  className="border-b border-slate-100 hover:bg-emerald-50/40 transition-colors cursor-pointer">
                  <td className="py-3 px-4 font-mono text-xs">
                    <div>{a.tanggal}</div>
                    <div className="text-slate-400">{a.waktu || "-"}</div>
                  </td>
                  <td className="py-3 px-4">
                    <p className="font-medium text-slate-900">{a.nama_pelanggan}</p>
                    <p className="text-xs text-slate-500">{a.no_hp}</p>
                  </td>
                  <td className="py-3 px-4 text-slate-800 font-medium">
                    {a.type_hp}
                    {(a.risk_flags || []).length > 0 && (
                      <span data-testid={`acq-risk-${a.id}`} title={a.risk_flags.join("\n")}
                        className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 text-[10px] font-semibold align-middle">
                        <AlertTriangle className="w-3 h-3" /> {a.risk_flags.length} risiko
                      </span>
                    )}
                  </td>
                  <td className="py-3 px-4" data-testid={`acq-jenis-${a.id}`}>
                    {a.trade_in ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-violet-100 text-violet-700 text-[10px] font-bold">
                        <ArrowLeftRight className="w-3 h-3" /> Trade-in
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 text-[10px] font-bold">
                        Terima Jual
                      </span>
                    )}
                  </td>
                  <td className="py-3 px-4 text-xs text-slate-600">
                    <p className="font-mono">{a.no_rekening || "-"}</p>
                    <p className="text-slate-500">{a.nama_bank || "-"}</p>
                  </td>
                  <td className="py-3 px-4 text-right font-mono font-semibold">{idr(a.harga_akuisisi)}</td>
                  <td className="py-3 px-4"><span className={`px-2 py-0.5 rounded-full text-xs font-medium ${qcStyle[a.qc_check] || ""}`}>{a.qc_check}</span></td>
                  <td className="py-3 px-4 text-slate-700">{a.nama_penerima}</td>
                  <td className="py-3 px-4 text-center">
                    {editCount > 0 ? (
                      <span data-testid={`acq-editmark-${a.id}`}
                        className="text-xs bg-violet-100 text-violet-800 px-2 py-0.5 rounded-full font-medium inline-flex items-center gap-1">
                        <Pencil className="w-3 h-3" /> {editCount}×
                      </span>
                    ) : <span className="text-xs text-slate-300">—</span>}
                  </td>
                  <td className="py-3 px-4 text-right">
                    {canEdit && (
                      editable ? (
                        <span data-testid={`acq-editable-${a.id}`}
                          className="text-[10px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5 inline-flex items-center gap-1">
                          <Clock className="w-3 h-3" /> {minutesLeft(a.created_at, editWindow)}m
                        </span>
                      ) : (
                        <span data-testid={`acq-locked-${a.id}`}
                          className="text-[10px] text-slate-400 bg-slate-50 border border-slate-200 rounded-full px-2 py-0.5 inline-flex items-center gap-1">
                          <Lock className="w-3 h-3" /> Terkunci
                        </span>
                      )
                    )}
                  </td>
                </tr>
              );
            })}
            {items.length === 0 && (
              <tr><td colSpan={10} className="text-center py-12 text-sm text-slate-500">
                <Smartphone className="w-8 h-8 text-slate-300 mx-auto mb-2" />Belum ada akuisisi periode ini
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Deep-link gagal dibuka (brief bos 14 Agu #2) — tautan dari
          RekonAkuisisi.jsx yang menunjuk transaksi terhapus/tak berhak/error,
          supaya pengguna tidak mendarat di halaman kosong tanpa penjelasan. */}
      {linkedAcqNotice && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm grid place-items-center p-4"
          onClick={() => setLinkedAcqNotice(null)}>
          <div className="bg-white rounded-2xl w-full max-w-md p-6" onClick={e => e.stopPropagation()}
            data-testid="acq-linked-notfound">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-rose-50 border border-rose-200 flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="w-5 h-5 text-rose-600" />
              </div>
              <div>
                <h3 className="font-display font-semibold text-slate-900">Transaksi Tidak Bisa Dibuka</h3>
                <p className="text-sm text-slate-600 mt-1">{linkedAcqNotice}</p>
              </div>
            </div>
            <button onClick={() => setLinkedAcqNotice(null)} data-testid="acq-linked-notfound-close"
              className="w-full h-10 rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-sm font-medium">
              Tutup
            </button>
          </div>
        </div>
      )}

      {/* DETAIL MODAL */}
      {detailId && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm grid place-items-center p-4 overflow-y-auto"
          onClick={() => { setDetailId(null); setDetail(null); }}>
          <div className="bg-white rounded-2xl w-full max-w-2xl my-8" onClick={e => e.stopPropagation()}
            data-testid="acq-detail-modal">
            <div className="sticky top-0 bg-slate-900 text-white px-6 py-4 flex items-center justify-between rounded-t-2xl z-10">
              <div className="flex items-center gap-2">
                <FileText className="w-5 h-5" />
                <h3 className="font-display font-semibold">Detail Akuisisi</h3>
              </div>
              <button onClick={() => { setDetailId(null); setDetail(null); }}
                className="p-2 hover:bg-white/20 rounded-lg" data-testid="acq-detail-close">
                <X className="w-4 h-4" />
              </button>
            </div>
            {!detail ? (
              <div className="p-6">
                <LoadingState loading jumlahKartu={1} data-testid="acq-detail-loading" />
              </div>
            ) : (
              <div className="p-6 space-y-5">
                <div className="flex items-start justify-between border-b border-slate-100 pb-4">
                  <div>
                    <p className="text-xs text-slate-500 uppercase tracking-wider">Pelanggan</p>
                    <p className="font-display text-lg font-bold text-slate-900">{detail.nama_pelanggan}</p>
                    <p className="text-sm text-slate-500 mt-1"><Phone className="w-3.5 h-3.5 inline mr-1" />{detail.no_hp || "-"}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-slate-500 uppercase tracking-wider">Waktu</p>
                    <p className="font-mono text-sm text-slate-900">{detail.tanggal}{detail.waktu ? ` · ${detail.waktu}` : ""}</p>
                    <p className="text-xs text-emerald-700 font-medium mt-1">{detail.branch || "-"}</p>
                  </div>
                </div>

                {(detail.risk_flags || []).length > 0 && (
                  <div data-testid="acq-detail-risks" className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                    <p className="text-xs font-semibold text-amber-800 flex items-center gap-1.5 mb-1">
                      <AlertTriangle className="w-3.5 h-3.5" /> Risk Flags — periksa sebelum diproses
                    </p>
                    <ul className="text-xs text-amber-900/90 space-y-0.5">
                      {detail.risk_flags.map((f, i) => <li key={i}>· {f}</li>)}
                    </ul>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4 text-sm">
                  <Field label="Type HP"><span className="font-semibold">{detail.type_hp}</span></Field>
                  <Field label="Harga Akuisisi"><span className="font-mono font-semibold">{idr(detail.harga_akuisisi)}</span></Field>
                  <Field label="Jenis Akuisisi">
                    <span data-testid="acq-detail-jenis">
                      {detail.trade_in ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-violet-100 text-violet-700 text-xs font-bold">
                          <ArrowLeftRight className="w-3.5 h-3.5" /> AU (Trade-in)
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 text-xs font-bold">
                          AU (Terima Jual)
                        </span>
                      )}
                    </span>
                  </Field>
                  <Field label="Akun Sumber Pembayaran">
                    <span className="inline-flex items-center gap-1 text-slate-700">
                      <Wallet className="w-3.5 h-3.5 text-slate-400" />
                      {kasBankAccounts.find(a => a.id === detail.sumber_akun_id)?.name || "— otomatis (metode+brand) —"}
                    </span>
                  </Field>
                  <Field label="IMEI"><span className="font-mono">{detail.imei || "-"}</span></Field>
                  <Field label="Storage · Grade">{(detail.storage || "-") + " · " + (detail.grade || "-")}</Field>
                  {detail.estimasi && (
                    <Field label={`Estimasi saat input (${detail.estimasi.tipe === "buyback" ? "Buyback" : "Trade-in"})`}>
                      <span className="font-mono text-xs">{idr(detail.estimasi.low)} — <b>{idr(detail.estimasi.recommended)}</b> — {idr(detail.estimasi.high)}</span>
                    </Field>
                  )}
                  <Field label="Rekening"><span className="font-mono">{detail.no_rekening || "-"}</span></Field>
                  <Field label="Nama Bank"><span className="inline-flex items-center gap-1"><Building className="w-3.5 h-3.5 text-slate-400" />{detail.nama_bank || "-"}</span></Field>
                  <Field label="QC Check">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${qcStyle[detail.qc_check] || ""}`}>
                      {detail.qc_check}
                    </span>
                  </Field>
                  <Field label="Kode Poin">
                    <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 text-xs">{detail.kode || "HP"}</span>
                  </Field>
                  <Field label="Catatan QC"><span className="text-slate-600">{detail.qc_notes || "-"}</span></Field>
                  <Field label="Penerima">{detail.nama_penerima}</Field>
                  <Field label="Catatan Umum"><span className="text-slate-600 italic">{detail.catatan || "-"}</span></Field>
                  <Field label="Input oleh"><span className="text-xs text-slate-600">{detail.created_by}</span></Field>
                </div>

                {detail.trade_in && (
                  <div data-testid="acq-detail-tradein" className="bg-violet-50 border border-violet-200 rounded-lg p-3">
                    <p className="text-xs font-semibold text-violet-800 flex items-center gap-1.5 mb-1">
                      <ArrowLeftRight className="w-3.5 h-3.5" /> Trade-in
                    </p>
                    {detail.trade_in_items && detail.trade_in_items.length > 0 ? (
                      <ul className="text-xs text-violet-900/90 space-y-0.5">
                        {detail.trade_in_items.map((t, i) => (
                          <li key={i}>· {t.nama || "(tanpa nama)"} {t.imei ? `— IMEI ${t.imei}` : ""} — <b>{idr(t.harga)}</b></li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-xs text-violet-900/90">
                        <b>Ya</b> — {detail.trade_in_note || <span className="italic text-slate-500">tanpa catatan</span>}
                      </p>
                    )}
                  </div>
                )}

                {detail.checklist && (
                  <div data-testid="acq-detail-checklist" className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                    <p className="text-xs font-semibold text-slate-700 flex items-center gap-1.5 mb-1.5">
                      <ClipboardList className="w-3.5 h-3.5 text-emerald-600" /> Checklist Kondisi
                    </p>
                    <div className="text-xs text-slate-600 grid grid-cols-2 sm:grid-cols-3 gap-y-1 gap-x-3">
                      <span>Layar: <b>{detail.checklist.screen_condition || "-"}</b></span>
                      <span>Body: <b>{detail.checklist.body_condition || "-"}</b></span>
                      <span>Baterai: <b>{detail.checklist.battery_health != null && detail.checklist.battery_health !== "" ? `${detail.checklist.battery_health}%` : "-"}</b></span>
                      <span>iCloud: <b className={detail.checklist.icloud_status === "belum_logout" ? "text-amber-700" : ""}>{ICLOUD_LABEL[detail.checklist.icloud_status] || "-"}</b></span>
                      <span>Find My: <b className={detail.checklist.find_my_status === "masih_aktif" ? "text-amber-700" : ""}>{FINDMY_LABEL[detail.checklist.find_my_status] || "-"}</b></span>
                      {CHECK_TOGGLES.map(([key, lbl]) => (
                        <span key={key}>{lbl}: <b className={detail.checklist[key] === false ? "text-rose-700" : "text-emerald-700"}>
                          {detail.checklist[key] === false ? "Bermasalah" : "OK"}</b></span>
                      ))}
                    </div>
                  </div>
                )}

                {(detail.edit_count || 0) > 0 && (
                  <div className="bg-violet-50 border border-violet-200 rounded-lg p-3">
                    <p className="text-xs font-semibold text-violet-800 flex items-center gap-1.5 mb-1">
                      <History className="w-3.5 h-3.5" /> Riwayat Edit ({detail.edit_count}×)
                    </p>
                    <ul className="text-xs text-violet-900/80 space-y-0.5">
                      {(detail.edit_history || []).map((h, i) => (
                        <li key={i}>· {formatTanggal(h.at, { denganJam: true })} — {h.by}</li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="flex justify-between items-center pt-2 border-t border-slate-100">
                  <div className="text-xs text-slate-500">
                    {detail._can_edit ? (
                      <span className="inline-flex items-center gap-1 text-emerald-700"><Clock className="w-3.5 h-3.5" /> Bisa diedit ~{minutesLeft(detail.created_at, detail._edit_window_minutes)} menit lagi</span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-slate-500"><Lock className="w-3.5 h-3.5" /> Waktu edit sudah lewat ({detail._edit_window_minutes}m)</span>
                    )}
                  </div>
                  <div className="flex gap-2">
                    {canEdit && (
                      <button onClick={() => del(detail.id)} data-testid="acq-detail-delete"
                        className="h-10 px-4 rounded-lg border border-rose-200 text-rose-600 hover:bg-rose-50 text-sm font-medium inline-flex items-center gap-1.5">
                        <Trash2 className="w-4 h-4" /> Hapus
                      </button>
                    )}
                    {canEdit && detail._can_edit && (
                      <button onClick={() => openEdit(detail)} data-testid="acq-detail-edit"
                        className="h-10 px-4 rounded-lg bg-[#0F172A] hover:bg-slate-800 text-white text-sm font-medium inline-flex items-center gap-1.5">
                        <Pencil className="w-4 h-4" /> Edit
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* CREATE / EDIT MODAL */}
      {modal && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm grid place-items-center p-4 overflow-y-auto"
          onClick={() => setModal(false)}>
          <div className="bg-white rounded-2xl w-full max-w-2xl my-8" onClick={e => e.stopPropagation()}>
            <div className="sticky top-0 bg-[#0F172A] text-white px-6 py-4 flex items-center justify-between rounded-t-2xl">
              <div className="flex items-center gap-2">
                <Smartphone className="w-5 h-5" />
                <h3 className="font-display font-semibold">{modalMode === "edit" ? "Edit Akuisisi" : "Akuisisi Unit"}</h3>
              </div>
              <button onClick={() => setModal(false)} className="p-2 hover:bg-white/20 rounded-lg">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                <F l="Tanggal"><DatePicker value={form.tanggal} onChange={v => setForm(s => ({ ...s, tanggal: v }))} data-testid="acq-tanggal" /></F>
                <F l="Waktu"><input type="time" value={form.waktu} onChange={e => setForm(s => ({ ...s, waktu: e.target.value }))} className={inp} data-testid="acq-waktu" /></F>
                <F l="Branch"><select value={form.branch} onChange={e => setForm(s => ({ ...s, branch: e.target.value }))} className={inp + " bg-white"}>
                  {BRANCHES.map(b => <option key={b} value={b}>{b || "— pilih —"}</option>)}
                </select></F>
                <F l="Nama Pelanggan *"><input value={form.nama_pelanggan} onChange={e => setForm(s => ({ ...s, nama_pelanggan: e.target.value }))} data-testid="acq-nama" className={inp} /></F>
                <F l="No HP"><input value={form.no_hp} onChange={e => setForm(s => ({ ...s, no_hp: e.target.value }))} className={inp} /></F>
                <F l="Type HP *"><input value={form.type_hp} onChange={e => setForm(s => ({ ...s, type_hp: e.target.value }))} placeholder="Contoh: iPhone 13 Pro" data-testid="acq-type" className={inp} /></F>
                <F l="IMEI"><input value={form.imei} onChange={e => setForm(s => ({ ...s, imei: e.target.value }))} placeholder="IMEI / SN (opsional, bentuk bebas)" data-testid="acq-imei" className={inp + " font-mono"} /></F>
                <F l="Storage"><select value={form.storage} onChange={e => setForm(s => ({ ...s, storage: e.target.value }))} data-testid="acq-storage" className={inp + " bg-white"}>
                  {STORAGES.map(st => <option key={st} value={st}>{st || "— storage —"}</option>)}
                </select></F>
                <F l="Grade Kondisi"><select value={form.grade} onChange={e => setForm(s => ({ ...s, grade: e.target.value }))} data-testid="acq-grade" className={inp + " bg-white"}>
                  {GRADES.map(g => <option key={g.v} value={g.v}>{g.label}</option>)}
                </select></F>
                <F l="Harga Akuisisi *"><InputRupiah value={form.harga_akuisisi || ""} onChange={n => setForm(s => ({ ...s, harga_akuisisi: n }))} testid="acq-harga" className={inp + " text-right font-mono"} /></F>
                <F l="Akun Sumber Pembayaran">
                  <select value={form.sumber_akun_id} onChange={e => setForm(s => ({ ...s, sumber_akun_id: e.target.value }))}
                    data-testid="acq-sumber-akun" className={inp + " bg-white"}>
                    <option value="">— otomatis (metode+brand) —</option>
                    {kasBankAccounts.map(a => (
                      <option key={a.id} value={a.id}>{a.code} · {a.name}</option>
                    ))}
                  </select>
                </F>
                <F l="No Rekening"><input value={form.no_rekening} onChange={e => setForm(s => ({ ...s, no_rekening: e.target.value }))} className={inp + " font-mono"} /></F>
                <F l="Nama Bank"><input value={form.nama_bank} onChange={e => setForm(s => ({ ...s, nama_bank: e.target.value }))} placeholder="BCA / BRI / Mandiri" className={inp} /></F>
                <F l="Kode Poin"><select value={form.kode} onChange={e => setForm(s => ({ ...s, kode: e.target.value }))} data-testid="acq-kode" className={inp + " bg-white"}>
                  <option value="HP">HP (10 poin)</option>
                  <option value="Device">Device (10 poin)</option>
                  <option value="Other">Other</option>
                </select></F>
                <F l="QC Check"><select value={form.qc_check} onChange={e => setForm(s => ({ ...s, qc_check: e.target.value }))} data-testid="acq-qc" className={inp + " bg-white"}>
                  {QC_STATUS.map(q => <option key={q}>{q}</option>)}
                </select></F>
                <F l="Catatan QC"><input value={form.qc_notes} onChange={e => setForm(s => ({ ...s, qc_notes: e.target.value }))} placeholder="Baterai 85%, layar mulus..." className={inp} /></F>
                <F l="Nama Penerima *">
                  <select value={form.nama_penerima} onChange={e => setForm(s => ({ ...s, nama_penerima: e.target.value }))}
                    data-testid="acq-penerima" className={inp + " bg-white"}>
                    <option value="">— pilih karyawan —</option>
                    {branchEmployees.map(e => (
                      <option key={e.id} value={e.nama}>
                        {e.nama}{e.branch ? ` · ${e.branch}` : ""}
                      </option>
                    ))}
                  </select>
                </F>
                <F l="Catatan"><input value={form.catatan} onChange={e => setForm(s => ({ ...s, catatan: e.target.value }))} className={inp} /></F>
              </div>

              {/* JENIS AKUISISI (Task D, poin 7 owner): Terima Jual (default) vs
                  Trade-in. Trade-in items MIRIP InputNotaAkuisisi.jsx supaya
                  kebiasaan input sama di jalur nota & langsung — unit-unit ini
                  ikut dibuatkan/dihidupkan-kembali di Inventory (Task B) DAN
                  ikut dihitung ke rekap poin 7. */}
              <div className="border border-slate-200 rounded-xl p-4 bg-violet-50/30">
                <label className="inline-flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={form.trade_in}
                    onChange={e => setForm(f => ({ ...f, trade_in: e.target.checked }))}
                    data-testid="acq-tradein-toggle"
                    className="w-4 h-4 rounded border-slate-300 text-violet-600 focus:ring-violet-600/40" />
                  <span className="font-medium text-sm inline-flex items-center gap-1.5">
                    <ArrowLeftRight className="w-4 h-4 text-violet-600" /> Trade-in? (bukan Terima Jual biasa)
                  </span>
                </label>
                {form.trade_in && (
                  <div className="mt-3 space-y-2">
                    <p className="text-xs text-slate-500">
                      Unit yang DIBERIKAN ke pelanggan sebagai bagian pembayaran akuisisi ini —
                      masing-masing masuk stok ber-IMEI (kalau IMEI diisi).
                    </p>
                    {(form.trade_in_items || []).map((t, i) => (
                      <div key={i} className="flex gap-2 items-center">
                        <input value={t.nama} onChange={e => setTradeItem(i, { nama: e.target.value })}
                          placeholder="Nama unit" data-testid={`acq-ti-nama-${i}`}
                          className={inp + " flex-1"} />
                        <InputRupiah value={t.harga || ""} onChange={n => setTradeItem(i, { harga: n })}
                          testid={`acq-ti-harga-${i}`} className={inp + " w-32 text-right font-mono"} />
                        <input value={t.imei} onChange={e => setTradeItem(i, { imei: e.target.value })}
                          placeholder="IMEI (opsional)" data-testid={`acq-ti-imei-${i}`}
                          className={inp + " w-40 font-mono"} />
                        <button type="button" onClick={() => rmTradeItem(i)}
                          disabled={(form.trade_in_items || []).length <= 1}
                          data-testid={`acq-ti-remove-${i}`}
                          className="p-2 rounded-lg border border-slate-200 text-rose-600 hover:bg-rose-50 disabled:opacity-30">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                    <button type="button" onClick={addTradeItem} data-testid="acq-ti-add"
                      className="inline-flex items-center gap-1.5 text-xs font-medium text-violet-700 hover:underline">
                      <Plus className="w-3.5 h-3.5" /> Tambah unit trade-in
                    </button>
                    <p className="text-xs text-slate-600 pt-1">
                      Total Trade-in: <span className="font-mono font-bold">{idr(tradeInTotal)}</span>
                    </p>
                  </div>
                )}
              </div>

              {/* ESTIMASI HARGA — opsional, memakai Type HP + Storage + Grade di atas */}
              <div className="border border-slate-200 rounded-xl p-4 bg-slate-50/60">
                <div className="flex flex-wrap items-center gap-2">
                  <select value={estTipe} onChange={e => setEstTipe(e.target.value)}
                    data-testid="acq-est-tipe" className="h-9 px-2 rounded-lg border border-slate-200 text-sm bg-white">
                    <option value="trade_in">Trade-in</option>
                    <option value="buyback">Buyback</option>
                  </select>
                  <button type="button" onClick={cekEstimasi} disabled={estLoading || !form.type_hp}
                    data-testid="btn-cek-estimasi"
                    className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-sm font-medium">
                    <Calculator className="w-4 h-4" /> {estLoading ? "Menghitung..." : "Cek Estimasi Harga"}
                  </button>
                  <span className="text-xs text-slate-500">memakai Type HP + Storage + Grade di atas</span>
                </div>
                {est && (
                  <div className="mt-3">
                    <div className="grid grid-cols-3 gap-2">
                      {[["low", "Bawah", est.low], ["recommended", "Rekomendasi", est.recommended], ["high", "Atas", est.high]].map(([k, lbl, v]) => (
                        <button key={k} type="button" title="Klik untuk isi Harga Akuisisi"
                          onClick={() => setForm(s => ({ ...s, harga_akuisisi: v }))}
                          data-testid={`acq-est-${k}`}
                          className={`rounded-lg border p-2 text-center hover:ring-2 hover:ring-emerald-600/40 ${k === "recommended" ? "border-emerald-300 bg-emerald-50" : "border-slate-200 bg-white"}`}>
                          <p className="text-[10px] uppercase tracking-wider text-slate-500">{lbl}</p>
                          <p className={`font-mono text-sm font-semibold ${k === "recommended" ? "text-emerald-700" : "text-slate-800"}`}>{idr(v)}</p>
                        </button>
                      ))}
                    </div>
                    <p className="mt-2 text-xs text-slate-500" data-testid="acq-est-source">
                      Basis {idr(est.basis)} · {est.basis_source === "rata2_aktual"
                        ? `rata-rata akuisisi aktual (${est.jumlah_sampel} sampel)`
                        : "tabel harga statis (config)"}
                      {!est.model_dikenal && <span className="text-amber-700 font-medium"> · model belum dikenal — angka kasar</span>}
                    </p>
                  </div>
                )}
              </div>

              {/* CHECKLIST KONDISI — opsional; menutupnya = tidak mengubah checklist tersimpan */}
              <div className="border border-slate-200 rounded-xl overflow-hidden">
                <button type="button" onClick={() => setShowChecklist(v => !v)}
                  data-testid="btn-toggle-checklist"
                  className="w-full flex items-center justify-between px-4 py-3 text-sm font-medium text-slate-700 hover:bg-slate-50">
                  <span className="inline-flex items-center gap-2"><ClipboardList className="w-4 h-4 text-emerald-600" /> Checklist Kondisi (opsional)</span>
                  {showChecklist ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </button>
                {showChecklist && (
                  <div className="px-4 pb-4 space-y-3 border-t border-slate-100 pt-3">
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                      <F l="Kondisi Layar"><select value={checklist.screen_condition} onChange={e => setChecklist(c => ({ ...c, screen_condition: e.target.value }))} data-testid="acq-cl-screen" className={inp + " bg-white"}>
                        {["Mulus", "Baret halus", "Minus / shadow", "Retak"].map(o => <option key={o}>{o}</option>)}
                      </select></F>
                      <F l="Kondisi Body"><select value={checklist.body_condition} onChange={e => setChecklist(c => ({ ...c, body_condition: e.target.value }))} data-testid="acq-cl-body" className={inp + " bg-white"}>
                        {["Mulus", "Lecet pemakaian", "Dent / penyok", "Bengkok"].map(o => <option key={o}>{o}</option>)}
                      </select></F>
                      <F l="Battery Health (%)"><input type="number" min="0" max="100" value={checklist.battery_health}
                        onChange={e => setChecklist(c => ({ ...c, battery_health: e.target.value }))}
                        placeholder="mis. 87" data-testid="acq-cl-battery" className={inp + " text-right font-mono"} /></F>
                      <F l="iCloud"><select value={checklist.icloud_status} onChange={e => setChecklist(c => ({ ...c, icloud_status: e.target.value }))} data-testid="acq-cl-icloud" className={inp + " bg-white"}>
                        {Object.entries(ICLOUD_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                      </select></F>
                      <F l="Find My iPhone"><select value={checklist.find_my_status} onChange={e => setChecklist(c => ({ ...c, find_my_status: e.target.value }))} data-testid="acq-cl-findmy" className={inp + " bg-white"}>
                        {Object.entries(FINDMY_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                      </select></F>
                    </div>
                    <div className="flex flex-wrap gap-x-5 gap-y-2">
                      {CHECK_TOGGLES.map(([key, lbl]) => (
                        <label key={key} className="inline-flex items-center gap-1.5 text-sm text-slate-700 cursor-pointer">
                          <input type="checkbox" checked={!!checklist[key]}
                            onChange={e => setChecklist(c => ({ ...c, [key]: e.target.checked }))}
                            data-testid={`acq-cl-${key}`}
                            className="w-4 h-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-600/40" />
                          {lbl}
                        </label>
                      ))}
                    </div>
                    <p className="text-xs text-slate-400">Centang = berfungsi normal. Grade keseluruhan mengikuti dropdown "Grade Kondisi" di atas.</p>
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
                <button onClick={() => setModal(false)} className="h-10 px-4 rounded-lg border border-slate-200 text-sm">Batal</button>
                <button onClick={save} data-testid="btn-save-acq" disabled={saving}
                  className="h-10 px-5 rounded-lg bg-[#0F172A] hover:bg-slate-800 disabled:opacity-50 text-white text-sm font-medium inline-flex items-center gap-2">
                  {modalMode === "edit" ? <Pencil className="w-4 h-4" /> : <Send className="w-4 h-4" />}
                  {saving ? "Menyimpan..." : (modalMode === "edit" ? "Simpan Perubahan" : "Simpan")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* PIN PERSETUJUAN HAPUS (brief #23) */}
      {pinDialog && (
        <div className="fixed inset-0 z-[70] bg-black/60 backdrop-blur-sm grid place-items-center p-4"
          onClick={() => setPinDialog(null)}>
          <div className="bg-white rounded-2xl w-full max-w-sm shadow-2xl" onClick={e => e.stopPropagation()}
            data-testid="pin-dialog">
            <div className="bg-rose-600 text-white px-5 py-3 rounded-t-2xl flex items-center justify-between">
              <h3 className="font-semibold text-sm inline-flex items-center gap-1.5">
                <Lock className="w-4 h-4" /> PIN Persetujuan Hapus
              </h3>
              <button onClick={() => setPinDialog(null)} className="p-1.5 hover:bg-white/20 rounded">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-5 space-y-3">
              <p className="text-xs text-slate-600">
                Penghapusan butuh persetujuan. Minta PIN ke pemegang izin (lihat Pusat Approval), lalu masukkan di sini.
              </p>
              <input value={pinDialog.pin}
                onChange={e => setPinDialog(d => ({ ...d, pin: e.target.value.replace(/\D/g, "").slice(0, 6), error: "" }))}
                maxLength={6} inputMode="numeric" placeholder="6 digit PIN" autoFocus
                data-testid="pin-input"
                className="w-full h-11 px-3 rounded-lg border border-slate-200 text-center text-lg tracking-[0.3em] font-mono" />
              {pinDialog.error && <p className="text-xs text-rose-600">{pinDialog.error}</p>}
            </div>
            <div className="border-t border-slate-100 p-4 flex justify-end gap-2">
              <button onClick={() => setPinDialog(null)} className="h-10 px-4 rounded-lg border border-slate-200 text-sm">
                Batal
              </button>
              <button onClick={submitPinDialog} disabled={pinDialog.submitting || pinDialog.pin.length !== 6}
                data-testid="pin-kirim"
                className="h-10 px-5 rounded-lg bg-rose-600 hover:bg-rose-700 disabled:opacity-50 text-white text-sm font-medium">
                {pinDialog.submitting ? "Memproses..." : "Kirim & Hapus"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const inp = "h-10 px-3 rounded-lg border border-slate-200 text-sm w-full focus:outline-none focus:ring-2 focus:ring-emerald-600/40";
function F({ l, children }) {
  return <label className="flex flex-col gap-1"><span className="text-xs font-medium text-slate-600">{l}</span>{children}</label>;
}
function Field({ label, children }) {
  return (
    <div>
      <p className="text-xs text-slate-500 uppercase tracking-wider mb-0.5">{label}</p>
      <div className="text-sm text-slate-900">{children}</div>
    </div>
  );
}
